/** @jsxImportSource @emotion/react */
import React, { useMemo } from "react";
import { Box, SxProps } from "@mui/material";
import { css, keyframes } from "@emotion/react";
import { vibrant } from "@repo/dashboard-constants";

const colorShift = keyframes`
  0%   { background-color: ${vibrant[800]}; }
  25%  { background-color: ${vibrant[700]}; }
  50%  { background-color: ${vibrant[600]}; }
  75%  { background-color: ${vibrant[700]}; }
  100% { background-color: ${vibrant[800]}; }
`;

const createSquareStyle = (duration: number, delay: number) => {
  return css`
    width: 100%;
    height: 100%;
    opacity: 0.3;
    animation: ${colorShift} ${duration}s ease-in-out infinite;
    animation-delay: ${delay}s;
  `;
};

const LoadingGrid = React.memo(() => {
  const squareStyles = useMemo(() => {
    const styles: ReturnType<typeof createSquareStyle>[] = [];
    for (let i = 0; i < 100; i++) {
      const duration = 6 + Math.random() * 6;
      const delay = Math.random() * duration;
      styles.push(createSquareStyle(duration, delay));
    }
    return styles;
  }, []);

  return (
    <Box
      sx={{
        display: "grid",
        width: "100%",
        height: "100%",
        gridTemplateColumns: "repeat(10, 1fr)",
        gridTemplateRows: "repeat(10, 1fr)",
      }}
    >
      {squareStyles.map((style, i) => (
        <Box key={i} css={style} />
      ))}
    </Box>
  );
});

LoadingGrid.displayName = "LoadingGrid";

const Cluster = React.memo(function Cluster({
  rows,
  cols,
  usedIndices,
  cellSize,
  gap,
  corner,
}: {
  rows: number;
  cols: number;
  usedIndices: number[];
  cellSize: number;
  gap: number;
  corner: "top-left" | "top-right" | "bottom-right" | "bottom-left";
}) {
  const total = rows * cols;
  const positions: React.CSSProperties =
    corner === "top-left"
      ? { top: 0, left: 0 }
      : corner === "top-right"
        ? { top: 0, right: 0 }
        : corner === "bottom-right"
          ? { bottom: 0, right: 0 }
          : { bottom: 0, left: 0 };

  const squareStyles = useMemo(() => {
    const styles: Record<number, ReturnType<typeof createSquareStyle>> = {};
    for (let i = 0; i < total; i++) {
      if (usedIndices.includes(i)) {
        const duration = 6 + Math.random() * 6;
        const delay = Math.random() * duration;
        styles[i] = createSquareStyle(duration, delay);
      }
    }
    return styles;
  }, [total, usedIndices]);

  return (
    <Box
      sx={{
        position: "absolute",
        width: cols * cellSize + (cols - 1) * gap,
        height: rows * cellSize + (rows - 1) * gap,
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
        gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
        gap: `${gap}px`,
        ...positions,
      }}
    >
      {Array.from({ length: total }).map((_, i) =>
        usedIndices.includes(i) ? (
          <Box key={i} css={squareStyles[i]} />
        ) : (
          <Box key={i} />
        ),
      )}
    </Box>
  );
});

Cluster.displayName = "Cluster";

const BackgroundLayer = React.memo(function BackgroundLayer({
  mode,
}: {
  mode: "loading" | "corner";
}) {
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        overflow: "hidden",
        background: `linear-gradient(to bottom, ${vibrant[900]}, ${vibrant[800]})`,
        pointerEvents: "none",
      }}
    >
      {mode === "loading" && <LoadingGrid />}
      {mode === "corner" && (
        <>
          <Cluster
            rows={3}
            cols={3}
            usedIndices={[0, 1, 2, 3, 4, 6]}
            cellSize={100}
            gap={1}
            corner="top-left"
          />
          <Cluster
            rows={3}
            cols={3}
            usedIndices={[0, 1, 2, 4, 5, 8]}
            cellSize={100}
            gap={1}
            corner="top-right"
          />
          <Cluster
            rows={3}
            cols={3}
            usedIndices={[2, 4, 5, 6, 7, 8]}
            cellSize={100}
            gap={1}
            corner="bottom-right"
          />
          <Cluster
            rows={3}
            cols={3}
            usedIndices={[0, 3, 4, 6, 7, 8]}
            cellSize={100}
            gap={1}
            corner="bottom-left"
          />
        </>
      )}
    </Box>
  );
});

BackgroundLayer.displayName = "BackgroundLayer";

type SquareBackgroundProps = {
  mode: "loading" | "corner";
  children?: React.ReactNode;
  sx?: SxProps;
};

export default function SquareBackground({
  mode,
  children,
  sx,
}: SquareBackgroundProps) {
  return (
    <>
      <BackgroundLayer mode={mode} />
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          ...(mode === "loading" && { minHeight: "100vh" }),
          ...sx,
        }}
      >
        {children}
      </Box>
    </>
  );
}
